import { useEffect, useState } from 'react';
import { Reveal } from '../components/Reveal';

interface Stop {
  key: string;
  eyebrow: string;
  title: string;
  body: string;
  /** 800px WebP — 1x displays */
  src: string;
  /** 1600px WebP — retina / 4K */
  src2x: string;
  alt: string;
}

/**
 * Product tour — four-stop carousel between WhySendFast and ForAgents.
 *
 * Layout:
 * - Left column: tab pills (Brand · Generate · Iterate · Ship) + a single
 *   active title/body that cross-fades on cycle. The stacked-grid trick
 *   (all 4 copies in the same grid cell, opacity-faded) auto-sizes the
 *   container to the longest copy — no magic min-height — and keeps the
 *   left column matched with the right column's height.
 * - Right column: faux-browser-chrome wrapper around a cross-faded image
 *   well. Aspect matches the raw screenshots (3024×1618 ≈ 1.87:1) so
 *   `object-cover` never crops the app chrome.
 *
 * Cycle: 6s auto-advance, paused while the section has hover/focus.
 * Click a pill to jump and pause manually.
 *
 * Adding / reordering stops: only `STOPS` needs to change. New images:
 * drop a PNG into `frontend/public/tour/`, run `npm run images:tour`, and
 * point the new `src` / `src2x` at the generated WebP variants.
 */
const STOPS: Stop[] = [
  {
    key: 'brand',
    eyebrow: 'Brand',
    title: 'Teach SendFast what "yours" means.',
    body:
      'Upload guidelines, paste past emails, pick your palette. Every draft after that comes out in your voice — no re-explaining the tone for the 30th time.',
    src: '/tour/brand.webp',
    src2x: '/tour/brand@2x.webp',
    alt: 'SendFast Brand page — brand profile (colors, voice, do/don\'t-say) on the left, uploaded brand files on the right.',
  },
  {
    key: 'generate',
    eyebrow: 'Generate',
    title: 'One prompt. A full HTML email.',
    body:
      'Type "welcome email for my brand". Claude streams a pixel-perfect, on-brand email into the editor — tables inlined, dark-mode-safe, ready to send.',
    src: '/tour/generate.webp',
    src2x: '/tour/generate@2x.webp',
    alt: 'SendFast editor with the Generate with AI panel open, brand voice active, ready to stream a new email.',
  },
  {
    key: 'tweak',
    eyebrow: 'Iterate',
    title: 'Tweak it together with AI.',
    body:
      'Every edit becomes a versioned draft. Rework the headline, swap a section, or A/B the CTA — without losing the version that already worked.',
    src: '/tour/tweak.webp',
    src2x: '/tour/tweak@2x.webp',
    alt: 'SendFast Drafts page showing multiple versioned drafts of a welcome email with Editor + AI Assist controls.',
  },
  {
    key: 'domain',
    eyebrow: 'Ship',
    title: 'Your domain, verified in one step.',
    body:
      'Add your domain, copy the DNS records, you\'re sending from you@yourbrand.com. No shared sender, no warmed-up pool you don\'t control.',
    src: '/tour/domain.webp',
    src2x: '/tour/domain@2x.webp',
    alt: 'SendFast Settings → Sending domains with sendfast.ai marked Verified and a Show DNS action.',
  },
];

const AUTO_ADVANCE_MS = 6000;

export function ProductTour() {
  const [index, setIndex] = useState(0);

  // Auto-advance every AUTO_ADVANCE_MS. The effect's dep on `index` means a
  // manual jump (handleJump → setIndex) re-runs the effect, clearing the
  // old timer and starting a fresh full-length one — the clicked slide
  // gets the full 6s, never the remainder of the previous slot.
  //
  // We deliberately do *not* pause on hover or focus. Pausing on hover
  // sounded nice but the section is large enough that visitors hover it
  // just by scrolling past, which made the progress bar look stuck at 0
  // every time it remounted into the paused state. Modern product
  // carousels (Linear / Stripe / Apple) just keep moving and let users
  // click to jump — same call here.
  useEffect(() => {
    const t = window.setTimeout(
      () => setIndex((i) => (i + 1) % STOPS.length),
      AUTO_ADVANCE_MS,
    );
    return () => window.clearTimeout(t);
  }, [index]);

  const handleJump = (i: number) => {
    setIndex(i);
  };

  return (
    <section id="tour" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <Reveal>
          <p className="text-muted-foreground text-xs uppercase tracking-[0.18em]">
            Meet SendFast
          </p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display mt-3 max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">
            From brand to inbox{' '}
            <span className="text-primary">in minutes.</span>
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed">
            One opinionated workflow — your brand teaches the model, AI drafts
            the email, you iterate together, then ship from your own domain.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-12">
          <Reveal className="lg:col-span-5" delay={180}>
            <div
              role="tablist"
              aria-label="Product tour navigation"
              className="flex flex-wrap gap-2"
            >
              {STOPS.map((s, i) => {
                const active = i === index;
                return (
                  <button
                    key={s.key}
                    type="button"
                    role="tab"
                    id={`tour-tab-${s.key}`}
                    aria-selected={active}
                    aria-controls="tour-panel"
                    onClick={() => handleJump(i)}
                    className={[
                      'rounded-full border px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] transition',
                      active
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                    ].join(' ')}
                  >
                    {s.eyebrow}
                  </button>
                );
              })}
            </div>

            {/* Stack all 4 copies in a single grid cell so the container
                auto-sizes to the longest one. Only the active copy is
                visible; the rest cross-fade in/out alongside the image. */}
            <div className="mt-8 grid">
              {STOPS.map((s, i) => (
                <div
                  key={s.key}
                  aria-hidden={i !== index}
                  className={[
                    'col-start-1 row-start-1 transition-opacity duration-500',
                    i === index ? 'opacity-100' : 'pointer-events-none opacity-0',
                  ].join(' ')}
                >
                  <h3 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
                    {s.title}
                  </h3>
                  <p className="text-muted-foreground mt-5 text-base leading-relaxed">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-border mt-8 h-px w-full overflow-hidden">
              <div
                // Remount on every cycle (key=index) so the animation
                // restarts cleanly from 0% without us tracking elapsed time.
                key={index}
                className="bg-primary tour-progress h-full"
                style={{ animationDuration: `${AUTO_ADVANCE_MS}ms` }}
              />
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={240}>
            <div
              id="tour-panel"
              role="tabpanel"
              aria-labelledby={`tour-tab-${STOPS[index].key}`}
              className="border-border bg-background relative overflow-hidden rounded-2xl border shadow-[0_40px_80px_-32px_rgba(11,12,15,0.18)]"
            >
              <div className="border-border bg-muted/60 flex items-center gap-1.5 border-b px-4 py-2.5">
                <span className="bg-foreground/15 size-2.5 rounded-full" aria-hidden />
                <span className="bg-foreground/15 size-2.5 rounded-full" aria-hidden />
                <span className="bg-foreground/15 size-2.5 rounded-full" aria-hidden />
                <span className="text-muted-foreground ml-3 select-none text-[11px] tracking-wide">
                  sendfast.ai/app
                </span>
              </div>

              {/* Aspect matches the raw screenshot dims (3024×1618 ≈ 1.87:1)
                  so `object-cover` doesn't trim the app chrome on the
                  sides. If we re-shoot screenshots later and the ratio
                  drifts, this is the one number to update. */}
              <div className="bg-muted/30 relative aspect-[3024/1618]">
                {STOPS.map((s, i) => (
                  <img
                    key={s.key}
                    src={s.src}
                    srcSet={`${s.src} 1x, ${s.src2x} 2x`}
                    alt={s.alt}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    className={[
                      'absolute inset-0 h-full w-full object-cover transition-opacity duration-500',
                      i === index ? 'opacity-100' : 'opacity-0',
                    ].join(' ')}
                  />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
